import { useState, useRef, useEffect } from "react";
import styles from "./EmailCodeConfirmModal.module.scss";
import Button from "../../button/Button";
import { useTranslation } from "react-i18next";
import { useVerifyEmailCodeMutation } from "../../../store/services/auth-api";
import close from "../../../assets/icons/close.svg";
import code from "../../../assets/pin-code.png";
import { useEmailUpdateRequestMutation } from "../../../store/services/auth-api";
import { useAppSelector } from "../../../store/hooks";
import { trackEvent } from "../../../utils/analytics";
import { EVENT_NAMES } from "../../../types/event-names";
import { updateUserEmail } from "../../../store/reducers/auth-reducer";

interface EmailCodeConfirmModalProps {
  setIsModalCodeConfirmVisible: (arg: boolean) => void;
  length: number;
  onComplete: (code: string) => void;
  setNewEmailAddress: (arg: string) => void;
  newEmailAddress: string;
  onEmailUpdate: (email: string) => void;
}

const EmailCodeConfirmModal: React.FC<EmailCodeConfirmModalProps> = ({
  setIsModalCodeConfirmVisible,
  length,
  onComplete,
  setNewEmailAddress,
  newEmailAddress,
  onEmailUpdate,
}) => {
  const isPaddle = useAppSelector((state) => state.auth.isPaddle);
  const [values, setValues] = useState<string[]>(Array(length).fill(""));
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [errorMessages, setErrorMessages] = useState<string>("");
  const [resendCoolDown, setResendCoolDown] = useState<number>(0);

  const { t } = useTranslation();
  const [emailUpdateRequest] = useEmailUpdateRequestMutation();
  const [verifyEmailCode] = useVerifyEmailCodeMutation();
  const title = t("Confirm your email address");
  const subtitle = t("Please enter the 6-digit code sent to");
  const message = "";

  const onConfirmClickhandler = async () => {
    const newValues = [...values];
    setIsLoader(true);
    if (newValues.every((val) => val !== "")) {
      await verifyEmailCode({
        code: newValues.join(""),
        new_email: newEmailAddress,
        processor: isPaddle ? "Paddle" : "FastSpring",
      })
        .unwrap()
        .then((res) => {
          trackEvent(EVENT_NAMES.CHANGE_EMAIL_VERIFIED);
          setIsLoader(false);
          setIsModalCodeConfirmVisible(false);
          setNewEmailAddress("");
          onEmailUpdate(newEmailAddress);

          trackEvent(EVENT_NAMES.CHANGE_EMAIL_SUCCESS);
          sessionStorage.setItem("email", newEmailAddress);
          updateUserEmail(newEmailAddress);
        })
        .catch((e) => {
          setErrorMessages("Invalid or expire code. Please try again.");
        });
    }
  };

  const handleChange = (value: string, index: number) => {
    if (isNaN(Number(value))) return; // Only allow numbers

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    // Move focus to the next input
    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    // If all fields are filled, call the onComplete callback
    if (newValues.every((val) => val !== "")) {
      onComplete(newValues.join(""));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !values[index] && index > 0) {
      // Move focus to the previous input if current is empty and Backspace is pressed
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = e.clipboardData.getData("Text").slice(0, length);
    if (!/^\d+$/.test(pastedData)) return; // Allow only numbers in paste

    const newValues = pastedData.split("").map((char, index) => (index < length ? char : ""));
    setValues(newValues);

    // Move focus to the last input
    inputsRef.current[Math.min(pastedData.length, length - 1)]?.focus();

    // If all fields are filled, call the onComplete callback
    if (newValues.length === length) {
      onComplete(newValues.join(""));
    }
  };

  const onClickResendEmailHandler = async () => {
    if (resendCoolDown > 0) return; // Prevent multiple clicks

    try {
      await emailUpdateRequest({ email: newEmailAddress }).unwrap();
      sessionStorage.setItem("email", newEmailAddress);
      setResendCoolDown(30);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (resendCoolDown === 0) return;

    const interval = setInterval(() => {
      setResendCoolDown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCoolDown]);

  return (
    <div className={styles.root}>
      <img src={close} alt="close" onClick={() => setIsModalCodeConfirmVisible!(false)} className={styles.root_close} />
      <img src={code} alt="code" />
      <p className={styles.root_title}>{title}</p>
      <div className={styles.root_info}>
        <p className={styles.root_subtitle}>{subtitle}</p>
        <p className={styles.root_message}>{message}</p>
      </div>
      <div className={styles.root_digits}>
        {values.map((value, index) => (
          <input
            key={index}
            ref={(el) => (inputsRef.current[index] = el!)}
            value={value}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            maxLength={1}
          />
        ))}
      </div>
      <div className={styles.root_footerSection}>
        <div className={styles.root_footer}>
          <Button
            onClick={onConfirmClickhandler}
            text={t("Confirm")}
            classNames={styles.root_footer_generate}
            isLoader={isLoader}
          />
          <p className={styles.root_footer_text}>{errorMessages}</p>
        </div>

        <div className={styles.root_footerSection_resendEmail}>
          <span
            className={`${styles.root_reSend} ${resendCoolDown > 0 ? styles.root_reSend_disabled : ""}`}
            onClick={onClickResendEmailHandler}
          >
            {resendCoolDown > 0 ? `${t("Re-send Email")}: ${resendCoolDown}` : t("Re-send Email")}
          </span>
          <div className={styles.root_footerSection_resendEmail_support}>
            <p>{t("Need Help?")}</p>
            <a
              onClick={() => {
                trackEvent(EVENT_NAMES.CONTACT_SUPPORT, {
                  source: "email_code_confirm",
                });
              }}
              href="https://www.fortect.com/support/"
              target="_blank"
              rel="noreferrer"
              className={styles.root_footer_link}
            >
              {" "}
              {t("Contact support")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCodeConfirmModal;
